import {
  Badge,
  Box,
  Button,
  CloseButton,
  Group,
  Popover,
  ScrollArea,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconChevronDown, IconSearch, IconTag } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { Dot, OrderTagGroup, TagChip } from "./OrderTagPicker";

/** Ô lọc đơn theo thẻ: chọn nhiều thẻ, thẻ có màu và xếp ngang theo danh mục */
const TagFilterSelect = ({
  groups,
  value,
  onChange,
  width = 240,
}: TagFilterSelectProps) => {
  const [opened, { toggle, close }] = useDisclosure(false);
  const [keySearch, setKeySearch] = useState("");

  const allTags = useMemo(
    () =>
      groups.flatMap((g) =>
        g.tags.map((t) => ({ ...t, categoryName: g.categoryName })),
      ),
    [groups],
  );
  const selectedTags = allTags.filter((t) => value.includes(t.id));

  const visibleGroups = useMemo(() => {
    const key = keySearch.trim().toLowerCase();
    return groups
      .map((g) => ({
        ...g,
        tags: g.tags
          .filter((t) => !t.inactive || value.includes(t.id))
          .filter((t) => !key || t.tagName.toLowerCase().includes(key)),
      }))
      .filter((g) => g.tags.length > 0);
  }, [groups, keySearch, value]);

  const toggleTag = (id: string) =>
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);

  const MAX_VISIBLE = 2;

  return (
    <Popover
      opened={opened}
      onChange={(o) => {
        if (!o) {
          close();
          setKeySearch("");
        }
      }}
      position="bottom-start"
      shadow="md"
      radius="md"
      width={380}
      trapFocus
    >
      <Popover.Target>
        <UnstyledButton
          aria-label="Lọc theo thẻ"
          aria-haspopup="dialog"
          onClick={toggle}
          style={{
            width,
            height: 36,
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "0 8px 0 10px",
            border: `1px solid ${opened ? "var(--mantine-color-blue-6)" : "var(--mantine-color-gray-4)"}`,
            borderRadius: 4,
            background: "#fff",
            overflow: "hidden",
          }}
        >
          <IconTag size={16} color="#868e96" style={{ flexShrink: 0 }} />
          <Group gap={4} wrap="nowrap" style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
            {selectedTags.length ? (
              <>
                {selectedTags.slice(0, MAX_VISIBLE).map((t) => (
                  <Badge
                    key={t.id}
                    variant="light"
                    radius="sm"
                    tt="none"
                    fw={500}
                    color={t.color || "blue"}
                    leftSection={<Dot color={t.color} />}
                    style={{ flexShrink: 1, minWidth: 0 }}
                  >
                    {t.tagName}
                  </Badge>
                ))}
                {selectedTags.length > MAX_VISIBLE ? (
                  <Badge variant="light" color="gray" radius="sm" style={{ flexShrink: 0 }}>
                    +{selectedTags.length - MAX_VISIBLE}
                  </Badge>
                ) : null}
              </>
            ) : (
              <Text size="sm" c="dimmed" truncate>
                Tất cả thẻ
              </Text>
            )}
          </Group>
          {value.length ? (
            <CloseButton
              size="sm"
              aria-label="Bỏ lọc thẻ"
              onClick={(e) => {
                e.stopPropagation();
                onChange([]);
              }}
            />
          ) : (
            <IconChevronDown size={16} color="#868e96" style={{ flexShrink: 0 }} />
          )}
        </UnstyledButton>
      </Popover.Target>

      <Popover.Dropdown p={8}>
        <TextInput
          size="sm"
          mb={6}
          placeholder="Tìm thẻ"
          rightSection={<IconSearch size={16} />}
          value={keySearch}
          onChange={(e) => setKeySearch(e.currentTarget.value)}
          data-autofocus
        />
        <ScrollArea.Autosize mah={340} type="auto">
          {visibleGroups.length ? (
            visibleGroups.map((g, idx) => (
              <Box key={g.categoryId} mt={idx === 0 ? 0 : 6}>
                <Text size="xs" fw={700} tt="uppercase" c="dark.4" px={4} pt={4} pb={2}>
                  {g.categoryName}
                </Text>
                <Group gap={6} px={4} pt={4} pb={4}>
                  {g.tags.map((t) => (
                    <TagChip
                      key={t.id}
                      name={t.tagName}
                      color={t.color}
                      checked={value.includes(t.id)}
                      inactive={t.inactive}
                      onClick={() => toggleTag(t.id)}
                    />
                  ))}
                </Group>
              </Box>
            ))
          ) : (
            <Text size="sm" c="dark.3" ta="center" py="md">
              {groups.length ? "Không tìm thấy thẻ" : "Chưa có thẻ cho đơn hàng"}
            </Text>
          )}
        </ScrollArea.Autosize>
        <Group justify="space-between" mt={8} px={4}>
          <Text size="xs" c="dark.4">
            Đã chọn {value.length} thẻ · đơn có 1 trong các thẻ
          </Text>
          <Button
            variant="subtle"
            color="red"
            size="compact-xs"
            disabled={!value.length}
            onClick={() => onChange([])}
          >
            Bỏ chọn
          </Button>
        </Group>
      </Popover.Dropdown>
    </Popover>
  );
};

export default TagFilterSelect;

type TagFilterSelectProps = {
  groups: OrderTagGroup[];
  value: string[];
  onChange: (value: string[]) => void;
  width?: number;
};
